import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router';
import './i18n';
import { AuthProvider } from './auth/AuthContext';
import { WizardStateProvider } from './wizard/WizardStateContext';
import ProtectedRoute from './auth/ProtectedRoute';
import AppShell from './layouts/AppShell';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import TwoFactorSetup from './pages/TwoFactorSetup';
import TwoFactorVerify from './pages/TwoFactorVerify';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import RallyeList from './pages/rallyes/RallyeList';
import RallyeCreate from './pages/rallyes/RallyeCreate';
import RallyeDetail from './pages/rallyes/RallyeDetail';
import StationLibraryList from './pages/stations/StationLibraryList';
import StationCreate from './pages/stations/StationCreate';
import StationDetail from './pages/stations/StationDetail';
import ChallengeLibraryList from './pages/challenges/ChallengeLibraryList';
import ChallengeCreate from './pages/challenges/ChallengeCreate';
import ChallengeDetail from './pages/challenges/ChallengeDetail';
import FindTeamStep from './pages/teamFlow/FindTeamStep';
import JoinTeamStep from './pages/teamFlow/JoinTeamStep';
import PickTeamNameStep from './pages/teamFlow/PickTeamNameStep';
import TeamNamePendingStep from './pages/teamFlow/TeamNamePendingStep';
import TeamWaitingStep from './pages/teamFlow/TeamWaitingStep';
import InviteTeamStep from './pages/teamFlow/InviteTeamStep';
import WelcomeStep from './pages/WelcomeStep';
import WelcomeGuard from './routes/WelcomeGuard';
import EntryChoiceStep from './pages/EntryChoiceStep';
import EntryChoiceGuard from './routes/EntryChoiceGuard';
import ProfileSetupStep from './pages/ProfileSetupStep';
import ProfileSetupRedirect from './routes/ProfileSetupRedirect';
import Register from './pages/Register';
import RunShell from './layouts/RunShell';
import TeamTab from './layouts/TeamTab';
import FeedbackForm from './pages/participant/FeedbackForm';
import StationList from './pages/participant/StationList';
import StationChallengeList from './pages/participant/StationChallengeList';
import ChallengeView from './pages/participant/ChallengeView';
import TeamChat from './pages/participant/TeamChat';
import TeamInfo from './pages/participant/TeamInfo';
import FeedView from './pages/participant/FeedView';
import ParticipantLeaderboard from './pages/participant/Leaderboard';
import RunHistory from './pages/participant/RunHistory';
import RunList from './pages/runs/RunList';
import RunAnalytics from './pages/analytics/RunAnalytics';
import RunCreate from './pages/runs/RunCreate';
import RunDashboard from './pages/runs/RunDashboard';
import CoordinatorTeamChat from './pages/runs/CoordinatorTeamChat';
import SubmissionReview from './pages/runs/SubmissionReview';
import Scoreboard from './pages/runs/Scoreboard';
import UserList from './pages/admin/UserList';
import OrgSettings from './pages/admin/OrgSettings';
import LogViewer from './pages/admin/LogViewer';
import Impressum from './pages/legal/Impressum';
import Privacy from './pages/legal/Privacy';
import Accessibility from './pages/legal/Accessibility';

function App() {
  return (
      <ErrorBoundary>
        <AuthProvider>
          <Router>
            <WizardStateProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              {/* Spec 026 (FR-017) — `/register` hosts the consolidated
                  ProfileSetupStep; the operator org-bootstrap form lives at
                  `/register/organization`. */}
              <Route
                path="/register"
                element={
                  <ProfileSetupRedirect>
                    <ProfileSetupStep guestModeDefault={false} />
                  </ProfileSetupRedirect>
                }
              />
              <Route
                path="/register/organization"
                element={<Register />}
              />
              <Route path="/2fa-setup" element={<TwoFactorSetup />} />
              <Route path="/2fa-verify" element={<TwoFactorVerify />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Spec 045 — invitees arrive here from their mail with no account yet. */}
              <Route path="/accept-invite" element={<AcceptInvite />} />
              {/* Spec 025 — Welcome screen as the public entrypoint. */}
              <Route path="/welcome" element={<WelcomeStep />} />
              {/* Spec 026 — entry-choice + profile-setup steps. */}
              <Route
                path="/auth-choice"
                element={
                  <EntryChoiceGuard>
                    <EntryChoiceStep />
                  </EntryChoiceGuard>
                }
              />
              <Route
                path="/join/:code/auth-choice"
                element={
                  <EntryChoiceGuard>
                    <EntryChoiceStep />
                  </EntryChoiceGuard>
                }
              />
              <Route
                path="/join/:code/profile"
                element={
                  <ProfileSetupRedirect>
                    <ProfileSetupStep guestModeDefault />
                  </ProfileSetupRedirect>
                }
              />
              {/* Spec 027 — /join is no longer a standalone page; redirect anything that lands there. */}
              <Route path="/join" element={<Navigate to="/welcome" replace />} />
              <Route
                path="/join/:code"
                element={
                  <WelcomeGuard>
                    <WelcomeStep />
                  </WelcomeGuard>
                }
              />
              {/* Spec 027 — team-flow nested routes (T018). Mutually exclusive
                  step children; the user enters via /join/:code/team/find after
                  ProfileSetup, then progresses based on their choice. */}
              <Route path="/join/:code/team" element={<Navigate to="find" replace />} />
              <Route path="/join/:code/team/find" element={<FindTeamStep />} />
              <Route path="/join/:code/team/join" element={<JoinTeamStep />} />
              <Route path="/join/:code/team/name" element={<PickTeamNameStep />} />
              <Route path="/join/:code/team/pending" element={<TeamNamePendingStep />} />
              <Route path="/join/:code/team/waiting" element={<TeamWaitingStep />} />
              <Route path="/join/:code/team/invite" element={<InviteTeamStep />} />
              <Route path="/legal/impressum" element={<Impressum />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/accessibility" element={<Accessibility />} />
              <Route
                path="/run-history"
                element={
                  <ProtectedRoute minRole="guest">
                    <RunHistory />
                  </ProtectedRoute>
                }
              />

              {/* Participant run shell — authenticated (incl. guests) */}
              <Route
                path="/runs/:runId/participant"
                element={
                  <ProtectedRoute minRole="guest">
                    <RunShell />
                  </ProtectedRoute>
                }
              >
                {/* Toni's 2026-07-23 test run (T1-4), Aaron's call: land on the
                    stations, not the feed. At run start the feed is empty and
                    carries no call to action, so the first thing a participant
                    sees is a blank page. The stations are the run. (#411) */}
                <Route index element={<Navigate to="stations" replace />} />
                <Route path="stations" element={<StationList />} />
                <Route path="feed" element={<FeedView />} />
                <Route path="team" element={<TeamTab />}>
                  {/* Aaron decided on 2026-09-19 (App#608): the Team tab opens on the
                      leaderboard. This reverses the 2026-07-14 walkthrough decision
                      (chat first) and matches TAB_ROUTE in participantNav.ts, which
                      already sent the bottom-nav button here. The leaderboard has to
                      make the own team obvious, that is App#610. */}
                  <Route index element={<Navigate to="leaderboard" replace />} />
                  <Route path="leaderboard" element={<ParticipantLeaderboard />} />
                  <Route path="chat" element={<TeamChat />} />
                  <Route path="info" element={<TeamInfo />} />
                </Route>
                {/* Backwards-compat redirects from the pre-Phase-2 IA */}
                <Route path="chat" element={<Navigate to="../team/chat" replace />} />
                <Route path="scoreboard" element={<Navigate to="../team/leaderboard" replace />} />
                <Route path="stations/:stationId" element={<StationChallengeList />} />
                <Route
                  path="stations/:stationId/challenges/:challengeId"
                  element={<ChallengeView />}
                />
                <Route path="feedback" element={<FeedbackForm />} />
              </Route>

              {/* Spec 025 — `/` shows the Welcome screen for guests; logged-in
                  users are redirected straight to /dashboard by WelcomeGuard. */}
              <Route
                path="/"
                element={
                  <WelcomeGuard>
                    <WelcomeStep />
                  </WelcomeGuard>
                }
              />

              {/* Protected routes with AppShell — path-less layout so `/` stays
                  free for the Welcome screen above. */}
              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<Profile />} />
                <Route
                  path="rallyes"
                  element={
                    <ProtectedRoute minRole="creator">
                      <RallyeList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rallyes/new"
                  element={
                    <ProtectedRoute minRole="creator">
                      <RallyeCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rallyes/:rallyeId"
                  element={
                    <ProtectedRoute minRole="creator">
                      <RallyeDetail />
                    </ProtectedRoute>
                  }
                />
                {/* Global Station library + composition */}
                <Route
                  path="stations"
                  element={
                    <ProtectedRoute minRole="creator">
                      <StationLibraryList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="stations/new"
                  element={
                    <ProtectedRoute minRole="creator">
                      <StationCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="stations/:stationId"
                  element={
                    <ProtectedRoute minRole="creator">
                      <StationDetail />
                    </ProtectedRoute>
                  }
                />
                {/* Global Challenge library */}
                <Route
                  path="challenges"
                  element={
                    <ProtectedRoute minRole="creator">
                      <ChallengeLibraryList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="challenges/new"
                  element={
                    <ProtectedRoute minRole="creator">
                      <ChallengeCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="challenges/:challengeId"
                  element={
                    <ProtectedRoute minRole="creator">
                      <ChallengeDetail />
                    </ProtectedRoute>
                  }
                />
                <Route path="runs" element={<ProtectedRoute minRole="coordinator"><RunList /></ProtectedRoute>} />
                <Route path="runs/new" element={<ProtectedRoute minRole="coordinator"><RunCreate /></ProtectedRoute>} />
                <Route path="runs/:runId" element={<ProtectedRoute minRole="coordinator"><RunDashboard /></ProtectedRoute>} />
                <Route path="runs/:runId/teams/:teamId/chat" element={<ProtectedRoute minRole="coordinator"><CoordinatorTeamChat /></ProtectedRoute>} />
                <Route path="runs/:runId/submissions" element={<ProtectedRoute minRole="coordinator"><SubmissionReview /></ProtectedRoute>} />
                <Route path="runs/:runId/scoreboard" element={<ProtectedRoute minRole="coordinator"><Scoreboard /></ProtectedRoute>} />
                <Route path="analytics" element={<ProtectedRoute minRole="coordinator"><RunAnalytics /></ProtectedRoute>} />
                <Route
                  path="admin/settings"
                  element={
                    <ProtectedRoute minRole="org_admin">
                      <OrgSettings />
                    </ProtectedRoute>
                  }
                />
                {/* Backwards-compat redirect for the old /settings path. */}
                <Route
                  path="settings"
                  element={<Navigate to="/admin/settings" replace />}
                />
                <Route
                  path="admin/users"
                  element={
                    <ProtectedRoute minRole="org_admin">
                      <UserList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/logs"
                  element={
                    <ProtectedRoute minRole="org_admin">
                      <LogViewer />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </WizardStateProvider>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
  );
}

export default App;
